import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbAlert, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { catchError, concatMap, from, map, of } from 'rxjs';
import {
  FileManagerService,
  ClearanceProfile,
  FlatNode
} from 'src/app/services/api/file-manager.service';
import { AlertInfo } from 'src/app/models/alert-info';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';

// Profili che vedono sempre tutti i file e le cartelle: non sono modificabili dal modal.
const ALWAYS_ENABLED_PROFILES = ['ADM1', 'QM01'];

@Component({
  selector: 'file-manager-clearance-modal',
  standalone: true,
  imports: [CommonModule, NgbAlert, LoaderComponent],
  templateUrl: './file-manager-clearance-modal.component.html',
  styleUrls: ['./file-manager-clearance-modal.component.scss']
})
export class FileManagerClearanceModalComponent implements OnChanges, OnDestroy {
  @ViewChild('content') private content!: TemplateRef<unknown>;

  @Input() node: FlatNode | null = null;
  @Output() closed = new EventEmitter<void>();

  profiles: ClearanceProfile[] = [];
  isLoading = false;
  isBulkSaving = false;
  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  private modalRef?: NgbModalRef;

  constructor(
    private fileManagerService: FileManagerService,
    private modalService: NgbModal,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  get canSelectAll(): boolean {
    return this.canRunBulk && this.editableProfiles.some(p => !p.enabled);
  }

  get canDeselectAll(): boolean {
    return this.canRunBulk && this.editableProfiles.some(p => p.enabled);
  }

  private get editableProfiles(): ClearanceProfile[] {
    return this.profiles.filter(p => !this.isAlwaysEnabled(p));
  }

  private get canRunBulk(): boolean {
    return !this.isBulkSaving && !this.profiles.some(p => p.loading);
  }

  isAlwaysEnabled(profile: ClearanceProfile): boolean {
    return ALWAYS_ENABLED_PROFILES.includes(profile.code);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['node']) {
      if (this.node) {
        this.loadProfiles();
        this.openModal();
      } else {
        this.profiles = [];
        this.alertInfo.show = false;
        this.closeModal();
      }
    }
  }

  ngOnDestroy(): void {
    this.closeModal();
  }

  private openModal(): void {
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.modalService.open(this.content, {
      centered: true,
      scrollable: true,
      windowClass: 'clearance-modal'
    });

    // Chiusura con ESC o click sul backdrop.
    this.modalRef.result.catch(() => {
      this.modalRef = undefined;
      if (this.node) {
        this.closed.emit();
      }
    });
  }

  private closeModal(): void {
    const ref = this.modalRef;
    this.modalRef = undefined;
    ref?.dismiss();
  }

  private loadProfiles(): void {
    if (!this.node?.path) return;
    this.isLoading = true;
    this.alertInfo.show = false;
    this.fileManagerService.getClearanceProfiles(this.node.path).subscribe({
      next: (profiles) => {
        this.profiles = profiles;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.showAlert('danger', 'Failed to load profiles. Close and try again.');
      }
    });
  }

  onToggle(profile: ClearanceProfile, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const newValue = checkbox.checked;
    profile.loading = true;

    this.fileManagerService.updateClearanceProfile(
      this.node!.path!,
      profile.code,
      newValue
    ).subscribe({
      next: () => {
        profile.enabled = newValue;
        profile.loading = false;
      },
      error: () => {
        checkbox.checked = !newValue;
        profile.loading = false;
        this.showAlert('danger', `Failed to update access for ${profile.code}.`);
      }
    });
  }

  selectAll(): void {
    if (!this.canSelectAll) return;
    this.applyToAll(true);
  }

  deselectAll(): void {
    if (!this.canDeselectAll) return;
    this.confirmationDialogService.open(
      `Are you sure you want to remove access to this ${this.node?.type === 'file' ? 'file' : 'folder'} ` +
      `for all profiles except ${ALWAYS_ENABLED_PROFILES.join(' and ')}?`,
      () => this.applyToAll(false)
    );
  }

  private applyToAll(enabled: boolean): void {
    const path = this.node?.path;
    const targets = this.editableProfiles.filter(p => p.enabled !== enabled);
    if (!path || targets.length === 0) return;

    this.isBulkSaving = true;
    this.alertInfo.show = false;
    targets.forEach(p => p.loading = true);
    const failed: string[] = [];

    from(targets).pipe(
      concatMap(profile =>
        this.fileManagerService.updateClearanceProfile(path, profile.code, enabled).pipe(
          map(() => ({ profile, ok: true })),
          catchError(() => of({ profile, ok: false }))
        )
      )
    ).subscribe({
      next: ({ profile, ok }) => {
        profile.loading = false;
        if (ok) {
          profile.enabled = enabled;
        } else {
          failed.push(profile.code);
        }
      },
      complete: () => {
        this.isBulkSaving = false;
        if (failed.length > 0) {
          this.showAlert('danger', `Failed to update access for ${failed.join(', ')}.`);
        }
      }
    });
  }

  close(): void {
    this.closed.emit();
  }

  private showAlert(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    setTimeout(() => { this.alertInfo.show = false; }, 3000);
  }
}
